// Ata cada `id` del catálogo de UI a su (paramType, key) en loan-config-business,
// con conversión de unidades, como booleano si es flag o como texto.
import { ParameterTypeEnum } from '@fiado/type-kit/bin/loanConfig/enums/ParameterTypeEnum';
import type { ParamValue } from './types';

export type { ParamValue };

export interface ParamBinding {
    paramType: ParameterTypeEnum;
    key: string;
    /** `boolean` viaja como 'true'/'false'; `text`, como texto; sin `kind` el valor es numérico. */
    kind?: 'boolean' | 'text';
    /** servidor → UI (default: identidad). */
    toUi?: (n: number) => number;
    /** UI → servidor (default: identidad). */
    toApi?: (n: number) => number;
}

const BOOLEAN_TRUE = 'true';
const BOOLEAN_FALSE = 'false';

/** Redondeo defensivo contra artefactos de float (0.15*100 → 15.000000000000002). */
export function clean(n: number): number {
    return Number(n.toFixed(4));
}

const secondsToMinutes: Pick<ParamBinding, 'toUi' | 'toApi'> = {
    toUi: (n) => n / 60,
    toApi: (n) => Math.round(n * 60),
};

const fractionToPercentInt: Pick<ParamBinding, 'toUi' | 'toApi'> = {
    toUi: (n) => n / 100,
    toApi: (n) => Math.round(n * 100 * 100) / 100,
};

export const PARAM_BINDINGS: { readonly [id: string]: ParamBinding | undefined } = {
    // ---------- 1 · WIZARD · ventanas de tiempo ----------
    'wiz.cotizacion_vigencia_min': {
        paramType: ParameterTypeEnum.WIZARD,
        key: 'quote_validity_minutes',
    },
    'wiz.reserva_producto_min': {
        paramType: ParameterTypeEnum.WIZARD,
        key: 'product_reservation_minutes',
    },
    'wiz.otp_timeout_min': {
        paramType: ParameterTypeEnum.WIZARD,
        key: 'otp_timeout_seconds',
        ...secondsToMinutes,
    },
    'wiz.sesion_horas': { paramType: ParameterTypeEnum.WIZARD, key: 'session_lifetime_hours' },
    'wiz.retry_score_meses': {
        paramType: ParameterTypeEnum.WIZARD,
        key: 'score_retry_veto_months',
    },
    // ---------- 2 · MOTOR · umbrales (pesos ya en fracción en el servidor) ----------
    'motor.sci_minimo_elegible': { paramType: ParameterTypeEnum.SCORING, key: 'sci_min_eligible' },
    'motor.peso_c2_nuevo': { paramType: ParameterTypeEnum.SCORING, key: 'layer2_weight_new' },
    'motor.peso_c2_recurrente': {
        paramType: ParameterTypeEnum.SCORING,
        key: 'layer2_weight_recurrent',
    },
    'motor.peso_c3_recurrente': {
        paramType: ParameterTypeEnum.SCORING,
        key: 'layer3_weight_recurrent',
    },
    // ---------- 3 · COBRANZA · SLAs y ciclo de mora ----------
    'cob.sla_excep_alta_horas': {
        paramType: ParameterTypeEnum.COLLECTIONS,
        key: 'exception_sla_high_hours',
    },
    'cob.sla_excep_media_horas': {
        paramType: ParameterTypeEnum.COLLECTIONS,
        key: 'exception_sla_medium_hours',
    },
    'cob.sla_excep_baja_horas': {
        paramType: ParameterTypeEnum.COLLECTIONS,
        key: 'exception_sla_low_hours',
    },
    'cob.dias_a_mora_dura': { paramType: ParameterTypeEnum.MORA_BLOCK, key: 'hard_mora_days' },
    'cob.dias_baja_contable': { paramType: ParameterTypeEnum.CREDIT, key: 'write_off_days' },
    // ---------- 4 · FRAUDE · detectores (el servidor guarda % entero) ----------
    'fraude.antifraude_mult_default': {
        paramType: ParameterTypeEnum.ANTI_FRAUD,
        key: 'store_collections_cap_factor',
    },
    'fraude.c1_tasa_rechazo_alta_pct': {
        paramType: ParameterTypeEnum.ANTI_FRAUD,
        key: 'c1_rejection_rate_pct',
        ...fractionToPercentInt,
    },
    'fraude.c2_mora_temprana_pct': {
        paramType: ParameterTypeEnum.ANTI_FRAUD,
        key: 'c2_early_mora_pct',
        ...fractionToPercentInt,
    },
    'fraude.c3_curps_duplicados': {
        paramType: ParameterTypeEnum.ANTI_FRAUD,
        key: 'c3_duplicate_curps_max',
    },
    'fraude.c4_ticket_atipico_delta': {
        paramType: ParameterTypeEnum.ANTI_FRAUD,
        key: 'c4_atypical_ticket_delta_pct',
        ...fractionToPercentInt,
    },
    // ---------- 5 · FEATURE FLAGS (wizard V2, efectivo y cross-border no tienen llave) ----------
    'flag.prospectos_multi_dia': {
        paramType: ParameterTypeEnum.FEATURE_FLAG,
        key: 'prospects_multi_day',
        kind: 'boolean',
    },
    'flag.trustonic_activo': {
        paramType: ParameterTypeEnum.FEATURE_FLAG,
        key: 'trustonic_enabled',
        kind: 'boolean',
    },
    // ---------- 6 · CONTRATO · datos impresos ----------
    'contrato.razon_social_proveedor': {
        paramType: ParameterTypeEnum.CREDIT,
        key: 'contract_provider_legal_name',
        kind: 'text',
    },
};

/** ¿Este parámetro de la pantalla tiene llave en el servidor? */
export function isBackedParam(id: string): boolean {
    return PARAM_BINDINGS[id] !== undefined;
}

/** Valor del wire → valor de pantalla. */
export function toUiValue(binding: ParamBinding, wire: string): ParamValue {
    if (binding.kind === 'boolean') return wire === BOOLEAN_TRUE;
    if (binding.kind === 'text') return wire;
    const toUi = binding.toUi ?? ((n: number) => n);
    return clean(toUi(Number(wire)));
}

/** Valor de pantalla → texto del wire. */
export function toWireValue(binding: ParamBinding, value: ParamValue): string {
    if (binding.kind === 'boolean') return value === true ? BOOLEAN_TRUE : BOOLEAN_FALSE;
    if (binding.kind === 'text') return String(value).trim();
    const toApi = binding.toApi ?? ((n: number) => n);
    return String(clean(toApi(Number(value))));
}
